import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useGetStickyHeight, useStickyHeight } from '@bucketplace/common';

import { isInApp } from '../utils';

interface UseScrollIntoAnchorProps {
  targetId?: string;
  initialAnchor: boolean;
  isLoading?: boolean;
}

interface UseScrollIntoAnchor {
  containerRef: MutableRefObject<HTMLDivElement | null>;
  anchorEnd?: boolean;
  scrollToTarget: (targetId: string | undefined, smooth?: boolean) => void;
}

export function useScrollIntoAnchor({
  targetId,
  isLoading,
  initialAnchor,
}: UseScrollIntoAnchorProps): UseScrollIntoAnchor {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const getStickyHeight = useGetStickyHeight();
  const isRedirect = useRef(false);
  const [anchorEnd, setAnchorEnd] = useState(!initialAnchor);
  const outerStickyData = useStickyHeight(containerRef, anchorEnd ? 'bottom' : 'top');

  const { current, min, max } = outerStickyData;

  const showGNB = isInApp() ? true : max !== 0 && (current === min || current === max);

  const clickAnchor = (targetId: string | undefined, smooth?: boolean): void => {
    setAnchorEnd(false);
    scrollToTarget(targetId, smooth);
  };

  const scrollToTarget = useCallback((targetId: string | undefined, smooth?: boolean) => {
    const targetElements = document.querySelectorAll(`#${targetId}`);
    const targetElement = targetElements?.[targetElements.length - 1];

    if (!targetElement) {
      return;
    }

    const bounds = targetElement.getBoundingClientRect();
    const top = Math.round(bounds.top);
    const { min, max } = outerStickyData;

    const absoluteTop = Math.round(window.pageYOffset + top);
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    const stickyOffset = getStickyHeight(absoluteTop - 1);

    if (stickyOffset === top) {
      setAnchorEnd(true);
      return;
    }

    const stickyHeight = stickyOffset - max;
    const direction = scrollY + max < absoluteTop ? 'up' : 'down';

    let diff;

    // 모바일 사이즈
    if (min === 0) {
      if (direction === 'up') {
        diff = -stickyHeight;
      } else {
        diff = -stickyOffset;
      }
      // 태블릿, 데스크탑 사이즈
    } else {
      if (direction === 'up') {
        diff = -max;
      } else {
        diff = -stickyOffset;
      }
    }

    window.scrollTo({
      top: absoluteTop + diff,
      behavior: smooth ? 'smooth' : undefined,
    });
    setAnchorEnd(true);
  }, [getStickyHeight, outerStickyData]);

  useEffect(() => {
    const perfEntries = performance.getEntriesByType('navigation');
    let initialLoad = false;
    /**
     * safari 15 버전부터 지원
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/PerformanceNavigationTiming/type}
     */
    if (typeof perfEntries === 'object') {
      const type = performance.navigation.type;
      initialLoad = type === PerformanceNavigation.TYPE_RELOAD || type === PerformanceNavigation.TYPE_NAVIGATE;
    } else {
      const type = (perfEntries[(perfEntries as PerformanceEntryList)?.length - 1] as PerformanceNavigationTiming).type;
      initialLoad = type === 'reload' || type === 'navigate';
    }

    if (initialLoad && initialAnchor && !isLoading && !isRedirect.current && showGNB) {
      window.history.scrollRestoration = 'manual';
      isRedirect.current = true;

      setTimeout(() => {
        scrollToTarget(targetId);
      }, 1000);
    }
  }, [initialAnchor, isLoading, showGNB, scrollToTarget, targetId]);

  useEffect(() => {
    window.history.scrollRestoration = 'auto';

    return () => {
      window.history.scrollRestoration = 'auto';
      isRedirect.current = false;
    };
  }, []);

  return {
    containerRef,
    scrollToTarget: clickAnchor,
    anchorEnd,
  };
}
